/**
 * Diagnostics request lifecycle (Phase H).
 *
 * For OCPP 2.0.1 prefer the dedicated diagnostics-request endpoint
 * (`POST /diagnostics-request/:chargerId`) which returns a `request_id`
 * the FE should poll. Legacy 1.6 chargers can also use the remote-control
 * `getDiagnostics` call but won't get a tracked request_id back.
 */

import React, { useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import OcppActionButton from '../components/OcppActionButton';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppKvList from '../components/OcppKvList';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import { requestDiagnostics } from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import type { CreateDiagnosticsRequestResponse } from '../models';
import type { OcppTabProps } from './types';

const DiagnosticsTab: React.FC<OcppTabProps> = ({ chargerId, ocppVersion }) => {
  const [uploadLocation, setUploadLocation] = useState<string>('');
  const [retries, setRetries] = useState<string>('3');
  const [retryInterval, setRetryInterval] = useState<string>('30');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [result, setResult] = useState<CreateDiagnosticsRequestResponse | null>(null);

  const onSubmit = async () => {
    setError(null);
    setResult(null);
    if (!uploadLocation.trim()) {
      setError({
        title: 'Upload location required',
        message: 'Provide a URL the charger can upload its diagnostics bundle to.',
        blockedByGate: false,
        featureGated: false,
        status: 400,
      });
      return;
    }
    setBusy(true);
    const res = await requestDiagnostics(chargerId, {
      upload_location: uploadLocation.trim(),
      retries: Number(retries) || undefined,
      retry_interval_seconds: Number(retryInterval) || undefined,
    });
    if (isOcppSuccess(res.status) && res.payload) {
      setResult(res.payload as CreateDiagnosticsRequestResponse);
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setBusy(false);
  };

  return (
    <View>
      {error ? <OcppErrorBanner error={error} /> : null}

      <OcppSectionCard
        title="Request diagnostics bundle"
        subtitle={
          ocppVersion === '1.6'
            ? 'OCPP 1.6 — uses GetDiagnostics; the result will not be tracked with a request_id.'
            : 'OCPP 2.0.1 — persists a tracked request_id you can poll the status of.'
        }
      >
        <Text style={styles.fieldLabel}>Upload location (URL)</Text>
        <TextInput
          value={uploadLocation}
          onChangeText={setUploadLocation}
          autoCapitalize="none"
          autoCorrect={false}
          placeholder="https://upload.example.com/diagnostics/"
          placeholderTextColor={OcppTheme.textDim}
          style={styles.input}
        />
        <View style={styles.inlineRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.fieldLabel}>Retries</Text>
            <TextInput
              value={retries}
              onChangeText={setRetries}
              keyboardType="numeric"
              style={styles.input}
              placeholderTextColor={OcppTheme.textDim}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.fieldLabel}>Retry interval (s)</Text>
            <TextInput
              value={retryInterval}
              onChangeText={setRetryInterval}
              keyboardType="numeric"
              style={styles.input}
              placeholderTextColor={OcppTheme.textDim}
            />
          </View>
        </View>

        <OcppActionButton
          label="Request diagnostics"
          icon="cloud-upload"
          onPress={onSubmit}
          loading={busy}
          style={{ marginTop: 14, alignSelf: 'flex-start' }}
        />

        {result ? (
          <View style={styles.resultBlock}>
            <OcppStatusPill label={result.status ?? 'Queued'} color={OcppTheme.success} />
            <View style={{ marginTop: 8 }}>
              <OcppKvList
                items={[
                  { key: 'Request ID', value: String(result.request_id) },
                  { key: 'File name', value: result.file_name ?? '—' },
                ]}
              />
            </View>
          </View>
        ) : null}
      </OcppSectionCard>
    </View>
  );
};

export default DiagnosticsTab;

const styles = StyleSheet.create({
  fieldLabel: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginBottom: 4,
    marginTop: 8,
  },
  input: {
    backgroundColor: OcppTheme.surfaceAlt,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
    paddingHorizontal: 12,
    paddingVertical: 8,
    color: OcppTheme.textPrimary,
    fontSize: 14,
  },
  inlineRow: {
    flexDirection: 'row',
    gap: 10,
  },
  resultBlock: {
    marginTop: 14,
    backgroundColor: OcppTheme.surfaceAlt,
    padding: 12,
    borderRadius: 10,
  },
});
